import React, { useEffect, useRef, useCallback } from 'react';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-luxon';
import { DateTime } from 'luxon';
import moment from 'moment-timezone';
import '../Wellness.less';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { useSelector } from 'react-redux';
import { getStartTime } from '@/utilities/helpers/commonHelper';

const BloodPressureChart = ({
  chartData,
  userTimezone,
  is24hoursformat,
  insightStartDateTime,
  insightEndDateTime,
  insightLoading,
  getDateFormatByLocation,
  selectedRange = 'today',
}) => {
  const canvasRef = useRef(null);

  const trans = useSelector(translator);

  const BAR_THICKNESS = 10;

  const getAggregatedData = useCallback(
    (chartDataFn) => {
      const dataArray = Array.isArray(chartDataFn)
        ? chartData.filter((item) => item !== null)
        : chartData
          ? [chartData]
          : [];

      const bloodPressureData = dataArray
        .map((item) => ({
          timestamp: item?.utc_timestamp_inms
            ? moment.utc(item.utc_timestamp_inms).tz(userTimezone).valueOf()
            : null,
          s: item?.data?.systolic || null,
          d: item?.data?.diastolic || null,
          p: item?.data?.pulse || null,
        }))
        .filter((item) => item.timestamp !== null)
        .sort((a, b) => a.timestamp - b.timestamp);

      let aggregatedData = [];

      aggregatedData = bloodPressureData.map((h) => {
        return {
          groupTimestamp: h.timestamp,
          actualTimestamp: h.timestamp,
          s: h.s,
          d: h.d,
          p: h.p,
        };
      });

      aggregatedData.sort((a, b) => a.groupTimestamp - b.groupTimestamp);

      const latestPerMinute = Object.values(
        aggregatedData.reduce((acc, item) => {
          const ts = new Date(item.actualTimestamp);
          const key = DateTime.fromJSDate(ts)
            .toUTC() // normalize timezone
            .toFormat('yyyy-MM-dd HH:mm');

          // keep latest for that minute
          if (!acc[key] || ts > new Date(acc[key].actualTimestamp)) {
            acc[key] = item;
          }

          return acc;
        }, {})
      );

      return latestPerMinute;
    },
    [chartData, userTimezone]
  );

  useEffect(() => {
    if (insightLoading) {
      return;
    }

    // Display timezone information.
    const tzAbbreviation = moment.tz(new Date(), userTimezone).format('z');
    const tzDisplayText = `${trans('TIMEZONE')}: ${userTimezone}`;

    const timeZonePlugin = {
      id: 'timeZonePlugin',
      afterDatasetsDraw: (chart, args, options) => {
        const { ctx, chartArea } = chart;
        const xAxis = chart.scales.x;
        const xAxisBottom = xAxis ? xAxis.bottom : chartArea.bottom;
        ctx.save();
        ctx.font = options.font || '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = options.color || 'black';
        const text = options.text || '';
        ctx.fillText(text, (chartArea.left + chartArea.right) / 2, xAxisBottom + 20);
        ctx.restore();
      },
    };

    Chart.register(timeZonePlugin);

    const aggregatedData = getAggregatedData(chartData);

    const labels = aggregatedData.map((r) => r.groupTimestamp);

    const data = aggregatedData.map((row) =>
      !isNaN(row.s) && !isNaN(row.d)
        ? { x: row.groupTimestamp, y: [row.s, row.d] }
        : { x: row.groupTimestamp, y: [NaN, NaN] }
    );

    const isToday = String(selectedRange || '').toLowerCase() === 'today';

    const chartJsData = {
      labels,
      rawTimestamps: aggregatedData.map((r) => r.actualTimestamp),
      datasets: [
        {
          label: trans('SYSTOLIC_DIASTOLIC'),
          data,
          yAxisID: 'bloodPressure',
          skipNull: true,
          backgroundColor: 'rgb(0, 150, 255)',
          hoverBackgroundColor: 'rgb(0, 75, 255)',
          barThickness: BAR_THICKNESS,
        },
        {
          label: trans('PULSE_BEATS'),
          data: aggregatedData.map((row) =>
            !isNaN(row.p) ? { x: row.groupTimestamp, y: row.p } : { x: row.groupTimestamp, y: NaN }
          ),
          yAxisID: 'pulse',
          type: 'line',
          spanGaps: true,
          borderWidth: 2,
          radius: 3,
          borderDash: [5, 5],
          borderColor: 'rgb(100, 100, 100)',
          backgroundColor: 'rgba(100, 100, 100, 0.2)',
        },
      ],
    };

    const chartObj = {
      type: 'bar',
      data: chartJsData,
      options: {
        animation: { duration: 0 },
        hover: { animationDuration: 0 },
        layout: { padding: { bottom: 30 } },
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'nearest', intersect: true },
        plugins: {
          title: { display: false },
          tooltip: {
            enabled: true,
            mode: 'nearest',
            axis: 'x',
            intersect: false,
            position: 'nearest',
            filter: (tooltipItem) => {
              // Skip if Systolic or Diastolic are NaN
              if (tooltipItem.datasetIndex === 0) {
                const systolic = tooltipItem.raw?.y?.[0];
                const diastolic = tooltipItem.raw?.y?.[1];
                return !isNaN(systolic) && !isNaN(diastolic);
              }
              // Skip if Pulse is NaN
              if (tooltipItem.datasetIndex === 1) {
                return !isNaN(tooltipItem.raw?.y ?? tooltipItem.raw);
              }
              return true;
            },
            callbacks: {
              title: (tooltipItems) => {
                if (!tooltipItems || tooltipItems?.length === 0) return '';
                const formatDate = getDateFormatByLocation(userTimezone);
                const formatString = is24hoursformat
                  ? `${formatDate}, HH:mm:ss`
                  : `${formatDate}, hh:mm:ss A`;
                const idx = tooltipItems[0].dataIndex;
                const actualTs = tooltipItems[0].chart.data.rawTimestamps[idx];
                return moment.tz(actualTs, userTimezone).format(formatString);
              },
              label: (tooltipItem) => {
                if (tooltipItem.datasetIndex === 0) {
                  const systolic = tooltipItem.raw && tooltipItem.raw.y && tooltipItem.raw.y[0];
                  const diastolic = tooltipItem.raw && tooltipItem.raw.y && tooltipItem.raw.y[1];
                  return `${trans('SYSTOLIC')}: ${systolic}, ${trans('DIASTOLIC')}: ${diastolic}`;
                }
                if (tooltipItem.datasetIndex === 1) {
                  return `${trans('PULSE')}: ${tooltipItem.formattedValue}`;
                }
              },
            },
          },
          datalabels: { display: false },
          timeZonePlugin: {
            text: tzDisplayText,
            font: '14px Arial',
            color: '#666',
          },
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: isToday ? 'hour' : 'day',
              displayFormats: isToday
                ? { hour: is24hoursformat ? 'HH:mm' : 'hh:mm a' }
                : { day: getDateFormatByLocation(userTimezone) },
              min: getStartTime({ selectedRange, userTimezone }),
              max: moment().tz(userTimezone).toDate(),
            },
            ticks: {
              callback: (value) => {
                const date = new Date(value);

                if (isToday) {
                  if (is24hoursformat) {
                    const time = moment.utc(date).tz(userTimezone).format('HH:mm');
                    if (time === '00:00') {
                      return '24:00';
                    }
                    return time;
                  }
                  return moment.utc(date).tz(userTimezone).format('ha');
                }

                return new Intl.DateTimeFormat('en', {
                  month: 'short',
                  day: 'numeric',
                }).format(date); // e.g. "Sep 1"
              },
            },
          },
          bloodPressure: {
            type: 'linear',
            display: true,
            position: 'left',
            title: { display: true, text: trans('BLOOD_PRESSURE_GRAPH_TITLE') },
            ticks: { color: 'rgb(0, 75, 255)' },
            grid: { drawOnChartArea: false },
          },
          pulse: {
            type: 'linear',
            display: true,
            position: 'right',
            title: { display: true, text: trans('PULSE_BEATS') },
            grid: { drawOnChartArea: false },
            beginAtZero: false,
            suggestedMax: 180,
          },
        },
      },
    };

    const chartInstance = new Chart(canvasRef.current, chartObj);

    return () => {
      chartInstance.destroy();
    };
  }, [
    chartData,
    userTimezone,
    is24hoursformat,
    insightStartDateTime,
    insightEndDateTime,
    insightLoading,
    selectedRange,
    getDateFormatByLocation,
  ]);

  return (
    <div className="chart-container">
      <canvas ref={canvasRef} className="chart-canvas" />
    </div>
  );
};

export default BloodPressureChart;
